import DashboardComponentWrapper from '../../components/dashboard/DashboardComponentWrapper';
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCallback, useEffect, useState } from "react";
import { handleSelectEmployer } from '../../features/employer/employerSlice';
import { useAppDispatch } from '../../app/hooks';
import Modal from '../../components/ui/Modal';
import Input, { CustomMultiSelect } from '../../components/ui/Input';
import { useGetUsersQuery } from '../../features/shared/usersApi';
import { useGetEmployersQuery, useCreateEmployerMutation, useUpdateEmployerMutation, useDeleteEmployerMutation } from '../../features/employer/employerApi';
import { hasAnyRole, useUserRoles } from '../../hooks/useCanViewApprovals';
import { jwtDecode } from "jwt-decode";
import { useLazyVerifyEmployerCodeQuery } from '../../features/auth/authApi';
import { LoaderIcon } from "react-hot-toast";
import toast from "react-hot-toast";
import { ChevronRight, Pencil, Plus, Trash2 } from "lucide-react";
import Button from '../../components/ui/Button';
import { logo } from '../../assets';

const EmployerList = () => {
    // Managed employers are an AccountOwner feature (the API returns 403 for other roles)
    const isAccountOwner = hasAnyRole(useUserRoles(), ['AccountOwner']);
    const { data: employersResponse, isLoading: loading, isError: employersError } = useGetEmployersQuery(undefined, { skip: !isAccountOwner });
    const employersList = Array.isArray(employersResponse) ? employersResponse : (employersResponse?.data || employersResponse?.items || []);
    const dispatch = useAppDispatch();
    const [createEmployer, { isLoading: isCreating }] = useCreateEmployerMutation();
    const [updateEmployer, { isLoading: isUpdating }] = useUpdateEmployerMutation();
    const [deleteEmployer, { isLoading: isDeleting }] = useDeleteEmployerMutation();
    const [isEdit, setIsEdit] = useState(false);
    const [showAddForm, setShowAddForm] = useState(false);
    const [confirmDelete, showConfirmDelete] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setParams] = useSearchParams();

    useEffect(() => {
        if (searchParams.get('open_form') === 'true') {
            setShowAddForm(true);
            setParams();
        }
    }, [searchParams]);

    const [data, setData] = useState({
        name: "",
        description: "",
        employerCode: "",
        emailAddress: "",
        includeInScheduleNotification: false,
        users: [],
    });
    const [employer, setEmployer] = useState<any>({});
    const [verifyEmployerCodeQuery, { isFetching: codeVerificationPending }] = useLazyVerifyEmployerCodeQuery();
    const [isCodeVerified, setIsCodeVerified] = useState(false);

    const handleChange = useCallback((name: string, value: string) => {
        if (name === 'employerCode') {
            setIsCodeVerified(false);
            setData((prevState) => ({
                ...prevState,
                [name]: value,
                name: '',
            }));
            return;
        }
        setData((prevState) => ({
            ...prevState,
            [name]: value
        }));
    }, []);

    const handleVerifyEmployerCode = async () => {
        try {
            const resp = await verifyEmployerCodeQuery(data.employerCode).unwrap();
            setIsCodeVerified(true);
            setData(prev => ({
                ...prev,
                name: resp.message
            }));
        } catch (err) {
            setIsCodeVerified(false);
            setData(prev => ({
                ...prev,
                name: ""
            }));
            // toast handled by middleware
        }
    };

    const { data: usersResponse } = useGetUsersQuery();
    const users = Array.isArray(usersResponse) ? usersResponse : (usersResponse?.data || usersResponse?.items || []);

    const handleAddEmployer = async (event) => {
        event.preventDefault();
        if (data.employerCode.trim().length > 0 && !isCodeVerified) {
            toast.error("Please verify the employer code before creating the employer.");
            return;
        }
        try {
            await createEmployer(data).unwrap();
            setShowAddForm(false);
            setIsCodeVerified(false);

            setData({
                name: "",
                description: "",
                employerCode: "",
                emailAddress: "",
                includeInScheduleNotification: false,
                users: [],
            });
        } catch (err) {
            // toast handled by middleware
        }
    };

    const editEmployerHandler = async (event) => {
        event.preventDefault();
        try {
            await updateEmployer({ ...employer, ...data }).unwrap();
            setShowAddForm(false);
            setIsEdit(false);
            setIsCodeVerified(false);

            setData({
                name: "",
                description: "",
                employerCode: "",
                emailAddress: "",
                includeInScheduleNotification: false,
                users: [],
            });
        } catch (err) {
            // toast handled by middleware
        }
    };

    const getUserOptions = users.map((user: any) => {
        return {
            label: `${user.firstName} ${user.lastName} [${user.roles}]`,
            value: user.userId
        }
    });

    const handleDeleteEmployer = (employer) => {
        setEmployer(employer);
        showConfirmDelete(true)
    };

    const deleteEmployerHandler = async () => {
        try {
            await deleteEmployer(employer.id).unwrap();
            showConfirmDelete(false);
            setEmployer({});
        } catch (err) {
            // handled
        }
    };

    const handleEditEmployer = (employer) => {
        setEmployer(employer);
        setData({
            name: employer.name,
            description: employer.description,
            employerCode: employer.employerCode,
            emailAddress: employer.emailAddress,
            includeInScheduleNotification: employer.includeInScheduleNotification,
            users: employer.users.map((itm) => itm.userId),
        });
        setIsCodeVerified(true);
        setShowAddForm(true);
        setIsEdit(true);
    };

    const getDefaultEmployer = () => {
        const token = localStorage.getItem('tazat');
        if (!token) return { name: '' };
        try {
            const decoded = jwtDecode<{ Profile?: string }>(token);
            const profile = decoded.Profile ? JSON.parse(decoded.Profile) : {};
            return { name: profile.CompanyName || '' };
        } catch {
            return { name: '' };
        }
    }

    return (
        <DashboardComponentWrapper
            subTitle={``}
            pageDetails=""
        >
            <>
                <div className="w-full flex flex-col justify-start items-center pb-10 min-h-screen">
                    <img
                        src={logo.black}
                        alt='PenSphere'
                        className='h-8 w-auto object-contain'
                    />
                    <div className="w-full max-w-6xl mb-8 flex flex-col gap-3">
                        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-neutral-900">Select an employer</h1>
                        <p className="text-base sm:text-lg text-neutral-500">Choose the organization you want to remit pension for today.</p>
                    </div>
                    <div className="w-full max-w-6xl my-2 flex flex-row flex-wrap items-stretch justify-start gap-5">
                        <div
                            onClick={() => setShowAddForm(true)}
                            className="cursor-pointer">
                            <div
                                className={`min-h-[220px] w-full sm:w-[300px] flex flex-col justify-between items-center p-7 rounded-2xl relative border border-dashed border-az-teal/40 bg-az-teal/[0.04] transition hover:-translate-y-1 hover:border-az-teal hover:bg-az-teal/[0.08]`}
                            >
                                <div className="flex flex-col justify-center items-center text-center gap-1">
                                    <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-az-teal shadow-sm"><Plus size={24} /></span>
                                    <p className="text-lg font-semibold text-neutral-800">Add another employer</p>
                                    <p className="text-sm leading-relaxed text-neutral-500">Manage pension remittances for another organization.</p>
                                </div>
                                <Button
                                    className="!w-full bg-az-teal text-white rounded-lg h-10 !pl-4 text-sm font-semibold flex flex-row justify-center items-center gap-2 mt-2"
                                    rightIcon={<Plus size={16} />}
                                >
                                    Add employer
                                </Button>
                            </div>
                        </div>
                        <div
                            className="cursor-pointer relative employer-card">
                            <div
                                className={`h-[220px] w-full sm:w-[300px] flex flex-col justify-between items-start p-7 rounded-2xl relative border border-az-teal/10 shadow-sm transition hover:-translate-y-1 hover:shadow-md`}
                                style={{
                                    backgroundColor: '#0898A01A',
                                }}
                            >
                                <div className="flex flex-col justify-start items-start">
                                    <p className="text-xs font-bold uppercase tracking-[0.14em] text-az-teal">Default account</p>
                                    <p className="mt-2 text-xl font-bold capitalize text-neutral-900 break-words">{getDefaultEmployer().name || 'Personal account'}</p>
                                </div>
                                <button
                                    onClick={() => {
                                        dispatch(handleSelectEmployer({}));
                                        navigate(`/dashboard/overview`);
                                    }}
                                    className="!w-full bg-az-teal text-white rounded-lg h-10 !pl-4 text-sm font-semibold flex flex-row justify-center items-center gap-2">
                                    Remit pension <ChevronRight size={16} />
                                </button>
                            </div>
                        </div>
                        {loading && <div className="w-full py-16 text-center text-neutral-500">Loading employers...</div>}
                        {employersError && <div className="w-full rounded-xl border border-red-200 bg-red-50 p-5 text-center text-sm text-red-700">Unable to load employers. Please refresh and try again.</div>}
                        {!loading && !employersError && employersList.length === 0 && <div className="w-full rounded-xl border border-neutral-200 bg-white p-8 text-center text-neutral-500">No additional employers yet. Add one to get started.</div>}
                        {!loading && !employersError && employersList?.map((itm, index) => (
                            <div
                                key={`${itm.title}${index}`}
                                className="cursor-pointer relative employer-card">
                                <div className="action-btn absolute flex-row gap-1 right-4 top-4 z-50">
                                    <button aria-label={`Delete ${itm.name}`} title="Delete employer" className="cursor-pointer rounded-md p-1.5 text-red-600 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-200" onClick={(e) => { e.stopPropagation(); handleDeleteEmployer(itm); }}>
                                        <Trash2 className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                    <button aria-label={`Edit ${itm.name}`} title="Edit employer" className="cursor-pointer rounded-md p-1.5 text-neutral-600 hover:bg-neutral-100 focus:outline-none focus:ring-2 focus:ring-az-teal/30" onClick={(e) => { e.stopPropagation(); handleEditEmployer(itm); }}>
                                        <Pencil className="w-4 h-4" aria-hidden="true" />
                                    </button>
                                </div>
                                <div
                                    className={`h-[220px] w-full sm:w-[300px] flex flex-col justify-between items-start p-7 rounded-2xl relative border border-az-teal/10 shadow-sm transition hover:-translate-y-1 hover:shadow-md`}
                                    style={{
                                        backgroundColor: '#0898A01A',
                                    }}
                                    title={itm.description}
                                >
                                    <div className="flex flex-col justify-start items-start">
                                        <p title={itm.name} className="text-xl font-bold capitalize w-full line-clamp-2 text-left text-neutral-900">{itm.name}</p>
                                        <p className="mt-2 text-sm font-semibold uppercase tracking-wider text-neutral-500">{itm.employerCode}</p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            dispatch(handleSelectEmployer(itm));
                                            navigate(`/dashboard/overview`);
                                        }}
                                        className="!w-full bg-az-teal text-white rounded-lg h-10 !pl-4 text-sm font-semibold flex flex-row justify-center items-center gap-2">
                                        Remit pension <ChevronRight size={16} />
                                    </button>
                                </div>
                            </div>
                        ))
                        }
                    </div>
                </div>
                <Modal
                    onClose={() => setShowAddForm(false)}
                    open={showAddForm}
                    title={isEdit ? 'Edit Employer' : 'Add new Employer'}
                    overrideStyle={{
                        height: 'fit-content',
                        maxHeight: '75vh'
                    }}
                >
                    <form onSubmit={isEdit ? editEmployerHandler : handleAddEmployer} className="w-full flex flex-col justify-start items-center h-full">
                        <div className="w-full flex flex-col sm:flex-row justify-between gap-2 sm:gap-4">
                            <div className="w-full sm:w-[48%] relative">
                                <Input
                                    name={"employerCode"}
                                    value={data.employerCode}
                                    label="Employer code"
                                    required
                                    onChange={handleChange}
                                    disabled={isEdit}
                                />
                                {data.employerCode.trim().length > 0 && !isCodeVerified && !isEdit && (
                                    <button
                                        type="button"
                                        onClick={handleVerifyEmployerCode}
                                        className="absolute top-[48%] right-[5%] !text-[12px] h-6 !px-3 !m-0 bg-az-teal text-white rounded"
                                        disabled={codeVerificationPending}
                                    >
                                        {codeVerificationPending ? <LoaderIcon /> : "Verify"}
                                    </button>
                                )}
                            </div>
                            <div className="w-full sm:w-[48%]">
                                <Input
                                    name={"name"}
                                    value={data.name}
                                    label="Employer name"
                                    required
                                    onChange={handleChange}
                                    disabled={isCodeVerified}
                                />
                                {codeVerificationPending && (
                                    <span className="text-xs text-slate-500 flex items-center gap-1 mt-1">
                                        <LoaderIcon /> Verifying employer code...
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="w-full flex flex-row justify-between">
                            <Input
                                name={"emailAddress"}
                                value={data.emailAddress}
                                label="Email Address"
                                required
                                onChange={handleChange}
                            />
                        </div>
                        <div className="w-full flex flex-col justify-between">
                            <label htmlFor="" className="w-full text-[#66676D]">Add users</label>
                            <CustomMultiSelect
                                data={getUserOptions}
                                // @ts-ignore
                                value={data.users.map((user) => {
                                    const obj = getUserOptions.find((itm) => itm.value === user);
                                    return obj;
                                }).filter((itm) => itm)}
                                onChange={(name, value) => {
                                    handleChange(name, value)
                                }}
                                name="users"
                            />
                        </div>
                        <div className="w-full flex flex-row justify-between">
                            <Input
                                name={"description"}
                                value={data.description}
                                label="Description"
                                onChange={handleChange}
                                maxLength={145}
                            />
                        </div>
                        <div className="w-full flex flex-row justify-start items-center mb-4">
                            <input
                                type="checkbox"
                                // @ts-ignore
                                onChange={(event) => handleChange('includeInScheduleNotification', event.target.checked)}
                                checked={data.includeInScheduleNotification}
                                className="!w-4 !h-4 mr-2 !mb-0" name="includeInScheduleNotification" id="" />
                            <label htmlFor="" className="w-full text-[#66676D]">Include in Schedule Notification</label>
                        </div>
                        <div className='flex flex-col-reverse sm:flex-row justify-around items-center gap-3 w-full'>
                            <button type="button" onClick={() => {
                                setShowAddForm(false);
                                setIsCodeVerified(false);

                                setData({
                                    name: "",
                                    description: "",
                                    employerCode: "",
                                    emailAddress: "",
                                    includeInScheduleNotification: false,
                                    users: [],
                                });
                            }} className='border border-az-teal text-az-teal px-4 py-2 w-full sm:w-[48%]'>Cancel</button>
                            <button disabled={isCreating || isUpdating} className='text-white bg-az-teal px-4 py-2 w-full sm:w-[48%] disabled:opacity-60'>{isCreating || isUpdating ? 'Saving...' : 'Submit'}</button>
                        </div>
                    </form>
                </Modal>
                <Modal
                    onClose={() => showConfirmDelete(false)}
                    open={confirmDelete}
                    title={'Confirm Delete Action'}
                    overrideStyle={{
                        height: '230px',
                    }}
                >
                    <div className="w-full flex flex-col justify-start items-center h-full">
                        <p className="text-center">Are you sure you want to delete this employer?</p>
                        <p className="text-center">You will not be able to access all employer data after deletion</p>
                        <div className='flex flex-row justify-around items-center w-full mt-5'>
                            <button disabled={isDeleting} onClick={() => showConfirmDelete(false)} className='border border-az-teal text-az-teal px-4 py-2 w-[48%] disabled:opacity-60'>No</button>
                            <button disabled={isDeleting} onClick={deleteEmployerHandler} className='text-white bg-az-teal px-4 py-2 w-[48%] disabled:opacity-60'>{isDeleting ? 'Deleting...' : 'Yes'}</button>
                        </div>
                    </div>
                </Modal>
            </>
        </DashboardComponentWrapper>
    )
};

export default EmployerList;
